/**
 * closeDateCheck.ts — the pure half of the ✦ Close date check card (deal
 * detail slice 8; spec §3.5 B; R2 of §39.9).
 *
 * SHOWN when the deal is open, has an expected close, and any of: the Stage
 * progress check fails, the close date is within 14 days, or it has passed.
 *
 * KEEP (R2) is stored on the server (close_date_confirmed_at / _for, 0133) and
 * this module reads it — nothing is device-local. The card stays HIDDEN while
 * BOTH hold:
 *   1. the confirmed date equals the deal's current expected close — so a
 *      changed close date asks again (R2: "not on a timer");
 *   2. the deal has NOT gone more than 14 days without activity — counted in
 *      Europe/London calendar days on the SERVER's today from the LATER of the
 *      Keep and the newest COMPLETED call, email or meeting — the server's,
 *      from the deal's assessment (rule set 2, §40.12: the same activities the
 *      Recent activity check counts). This is the spec's "unless the deal goes 14
 *      days without activity": a READ rule over the confirmation's timestamp
 *      and the activities, not a second store (R2). The boundary matches the
 *      Recent activity check: 14 quiet days still pass, the 15th asks again.
 */
import type { DealDetail, DealStage } from '../../types'
import type { DealAssessment } from '../../../../api/types'
import { STAGE_STEPS } from './stageBar'
import { closeDay, daysBetween, lastLoggedDay, londonDayOf, stagesLeft, RECENT_ACTIVITY_DAYS, type HealthCheck } from './dealHealthCard'

/** The card's window before the close date (spec: "within 14 days"). */
export const CLOSE_SOON_DAYS = 14

export interface CloseDateCardView {
  /** The calendar tile: "DEC" and "1". */
  month: string
  day: string
  /** "Tue 1 Dec 2026 · in 46 days" / "· today" / "· 3 days ago". */
  dateLine: string
  reason: string
  /** "Keep 1 Dec". */
  keepLabel: string
}

const plural = (n: number, one: string, many: string) => (n === 1 ? `${n} ${one}` : `${n} ${many}`)
const stageWord = (s: DealStage) => (s === 'New Lead' ? 'New lead' : s)

function dayParts(iso: string) {
  const d = new Date(`${iso}T00:00:00Z`)
  const f = (o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat('en-GB', { timeZone: 'UTC', ...o }).format(d)
  return { weekday: f({ weekday: 'short' }), day: f({ day: 'numeric' }), month: f({ month: 'short' }), year: f({ year: 'numeric' }) }
}

/** Is the Keep still in force? (rules 1 and 2 of the header.) */
export function keepInForce(
  deal: Pick<DealDetail, 'expectedClose' | 'closeDateConfirmedAt' | 'closeDateConfirmedFor'>,
  assessment: DealAssessment | null | undefined, todayIso: string,
): boolean {
  const close = closeDay(deal.expectedClose)
  const confirmedFor = closeDay(deal.closeDateConfirmedFor)
  const keptOn = londonDayOf(deal.closeDateConfirmedAt)
  if (!close || !confirmedFor || !keptOn || confirmedFor !== close) return false
  const logged = lastLoggedDay(assessment)
  const from = logged && logged > keptOn ? logged : keptOn
  return daysBetween(from, todayIso) <= RECENT_ACTIVITY_DAYS
}

/** The card's content, or null when it is not shown. */
export function closeDateCard({ deal, checks, assessment, todayIso }: {
  deal: Pick<DealDetail, 'stage' | 'expectedClose' | 'closeDateConfirmedAt' | 'closeDateConfirmedFor'>
  checks: readonly HealthCheck[]
  assessment: DealAssessment | null | undefined
  todayIso: string
}): CloseDateCardView | null {
  if (stagesLeft(deal.stage) == null) return null // closed (or unknown): no card
  const close = closeDay(deal.expectedClose)
  if (!close) return null // no date: the Close date check says so; nothing to keep
  const toClose = daysBetween(todayIso, close)
  const progressFails = checks.find(c => c.id === 'stageProgress')?.pass === false
  const soon = toClose >= 0 && toClose <= CLOSE_SOON_DAYS
  const passed = toClose < 0
  if (!progressFails && !soon && !passed) return null
  if (keepInForce(deal, assessment, todayIso)) return null

  const p = dayParts(close)
  const when = toClose > 0 ? `in ${plural(toClose, 'day', 'days')}` : toClose === 0 ? 'today' : `${plural(-toClose, 'day', 'days')} ago`
  const left = stagesLeft(deal.stage) ?? 0
  const first = STAGE_STEPS.indexOf(deal.stage) === 0
  let reason: string
  if (passed) {
    reason = `The close date passed ${plural(-toClose, 'day', 'days')} ago. Move the date or close the deal.`
  } else if (progressFails) {
    reason = first
      ? `Still in the first stage with ${plural(left, 'stage', 'stages')} to go. Plan the next steps or move the date.`
      : `Still in ${stageWord(deal.stage)} with ${plural(left, 'stage', 'stages')} to go. Plan the next steps or move the date.`
  } else {
    reason = toClose === 0
      ? `Closes today, still in ${stageWord(deal.stage)}. Plan the last steps or move the date.`
      : `Closes in ${plural(toClose, 'day', 'days')}, still in ${stageWord(deal.stage)}. Plan the last steps or move the date.`
  }
  return {
    month: p.month.toUpperCase(), day: p.day,
    dateLine: `${p.weekday} ${p.day} ${p.month} ${p.year} · ${when}`,
    reason,
    keepLabel: `Keep ${p.day} ${p.month}`,
  }
}

/** The sentence shown after Keep (spec §3.5 B, verbatim). */
export const KEPT_SENTENCE = "Kept. You won't be asked again unless the deal goes 14 days without activity."
